import { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { loader } from "@monaco-editor/react";
import { Button, AppBar, Toolbar } from "@material-ui/core";
import NavLink, {
  usePushHistory,
  useReplaceHistory,
} from "./components/NavLink";

import CustomRoutes from "./plugins/CustomRoutes";
import OnlyExecutionPage from "./pages/execution/OnlyExecutionPage";
import DefaultLoader from "./components/DefaultLoader";
import { useTabsList } from "./contexts/TabsContext.js";
import AccessDenied from "./components/AccessDenied";
import ErrorScreen from "./components/ErrorScreen";
import ProtectedRoute, {
  KITCHEN_ROUTES,
  ROUTE_CONFIG,
} from "./utils/ProtectedRoute";
import useFirstAvailableRoute from "./hooks/useFirstAvailableRoute";
import { pathToNavMap, routeMapping } from "./utils/constants";
import { getSafeMessage } from "./utils/helpers";
import { useMonacoStatus } from "./contexts/MonacoStatusContext";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#fafafa",
    display: "flex",
    paddingLeft: "0",
  },
  body: {
    width: "100vw",
    height: "100vh",
    paddingTop: theme.overrides.MuiAppBar.root.height,
  },
  body2: {
    width: "100vw",
    height: "100vh",
  },
  toolbarRight: {
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
  },
  toolbarRegular: {
    minHeight: theme.overrides.MuiAppBar.root.height,
    paddingLeft: "0px",
  },
  menuBreadCrumb: {
    "& nav": {
      height: "100%",
    },
    "& ol": {
      height: "100%",
    },
  },
  menuBar: {
    height: "100%",
    width: 64,
  },
  navBtn: {
    fontSize: "15px",
    fontWeight: "600",
    fontFamily: "Poppins",
    height: "36px",
    padding: "8px 16px",
    margin: "0 0px",
    textTransform: "none",
    border: "none",
    transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
    position: "relative",
    minWidth: "64px",
    "&:before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "50%",
      width: 0,
      height: "3px",
      backgroundColor: theme.palette.primary.main,
      transition: "all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
      transform: "translateX(-50%)",
    },
    "& .MuiButton-label": {
      color: "rgba(0, 0, 0, 0.87)",
      fontWeight: "500",
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      "& .MuiButton-label": {
        color: theme.palette.primary.main,
      },
    },
    "&:focus": {
      outline: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  },
  active: {
    "&:before": {
      width: "100%",
    },
    "& .MuiButton-label": {
      color: theme.palette.primary.main,
      fontWeight: "600",
    },
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.12)",
    },
  },
  notAuthenticated: {
    width: "100vw",
    height: "100vh",
    paddingTop: theme.overrides.MuiAppBar.root.height,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export default function App({ appData, flowRedirectUrl }) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = usePushHistory();
  const { setMonacoStatus } = useMonacoStatus();

  const [activeNav, setActiveNav] = useState("execution");
  const [requestID, setRequestID] = useState(null);
  const [loadingState, setLoadingState] = useState({
    isLoading: false,
    view: "",
  });

  const isTabActive = useCallback(
    (tab) => tab?.active === "True" && tab?.hide === "False",
    []
  );

  const [flowComplete, setFlowComplete] = useState(false);
  const {
    tabsList,
    isLoading: tabsLoading,
    error: tabsError,
    fetchTabsList,
  } = useTabsList();

  useEffect(() => {
    loader.config({
      paths: {
        vs: "/monaco-editor/min/vs", // served from public/
      },
    });

    const handleMonacoLoadError = (event) => {
      const isMonacoError = event.target?.src?.includes("/min/vs/");
      if (isMonacoError) {
        console.error("❌ Monaco Editor script failed to load.", event);
        setMonacoStatus("failed");
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handleMonacoLoadError, true);

    try {
      loader
        .init()
        .then((monaco) => {
          console.log("✅ Monaco loaded successfully.");
          setMonacoStatus("ready");
        })
        .catch((error) => {
          console.error("❌ Monaco loader promise was rejected:", error);
          setMonacoStatus("failed");
        })
        .finally(() => {
          window.removeEventListener("error", handleMonacoLoadError, true);
        });
    } catch (err) {
      console.error("❌ Unexpected error while initializing Monaco:", err);
      setMonacoStatus("failed");
    }

    return () => {
      window.removeEventListener("error", handleMonacoLoadError, true);
    };
  }, [setMonacoStatus]);

  useEffect(() => {
    const segments = location.pathname
      .split("/")
      .filter((segment) => segment !== "");

    if (pathToNavMap[location.pathname]) {
      setActiveNav(pathToNavMap[location.pathname]);
      return;
    }

    const firstSegment = segments[0];
    if (firstSegment === "execution") {
      setActiveNav("ex");
    } else if (firstSegment === "workflowDef" || firstSegment === "taskDef") {
      setActiveNav("def");
    } else if (firstSegment === "taskQueue") {
      setActiveNav("tskque");
    } else if (firstSegment === "admin") {
      setActiveNav("admin");
    } else {
      setActiveNav("");
    }
  }, [location.pathname]);

  const responseFunction = useCallback(
    (event) => {
      let { data } = event;
      if (window.location.host.includes("localhost")) {
        setLoadingState({ isLoading: false, view: "FLOW_MAIN" });
        setRequestID("");
      } else {
        if (data?.unique_id) {
          if (data?.requestId) {
            setRequestID(event.data.requestId);
            navigate(`/ticket_summary/${event.data.requestId}`);
            setLoadingState({ isLoading: false, view: "TICKET_SUMMARY" });
            window.removeEventListener("message", responseFunction);
          } else if (data?.flowRedirectUrl) {
            setLoadingState({ isLoading: false, view: "FLOW_MAIN" });
            setRequestID("");
            navigate(data?.flowRedirectUrl);
          } else {
            setLoadingState({ isLoading: false, view: "FLOW_MAIN" });
            setRequestID("");
          }
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    setLoadingState({ isLoading: true, view: "" });
    if (
      appData ||
      (window.location.host.includes("localhost") && !flowComplete)
    ) {
      responseFunction({ data: appData, flowRedirectUrl: flowRedirectUrl });
    } else if (window.location === window.parent.location) {
      setLoadingState({ isLoading: false, view: "" });
    }
  }, [appData, flowRedirectUrl, flowComplete, responseFunction]);

  useEffect(() => {
    if (loadingState.view === "FLOW_MAIN") {
      fetchTabsList();
    }
  }, [loadingState.view, fetchTabsList]);

  const getFirstAvailableRoute = useFirstAvailableRoute(
    tabsList,
    isTabActive,
    routeMapping
  );

  const renderRoutes = useMemo(() => {
    const routes = [];

    const addRoute = (path, key, component, tabType) => {
      let permissions = null;

      if (tabsList) {
        if (tabType.includes(".")) {
          const [parentType, childType] = tabType.split(".");
          permissions = tabsList?.[parentType]?.[childType] || null;
        } else {
          permissions = tabsList?.[tabType] || null;
        }
      }

      routes.push(
        <ProtectedRoute
          exact
          path={path}
          key={key}
          component={component}
          permissions={permissions}
        />
      );
    };

    ROUTE_CONFIG.forEach(({ path, key, component, tabType }) => {
      addRoute(path, key, component, tabType);
    });

    KITCHEN_ROUTES.forEach(({ path, key, component }) => {
      routes.push(<Route exact path={path} key={key} component={component} />);
    });

    routes.push(<CustomRoutes key="custom-routes" />);

    return routes;
  }, [tabsList]);

  const renderNavButtons = useMemo(() => {
    return (
      <>
        {isTabActive(tabsList?.Execution) && (
          <Button
            id="exeTab"
            className={`${classes.navBtn} ${
              activeNav === "ex" ? classes.active : ""
            }`}
            component={NavLink}
            path="/"
          >
            Executions
          </Button>
        )}
        {isTabActive(tabsList?.Definition) && (
          <Button
            id="defTab"
            className={`${classes.navBtn} ${
              activeNav === "def" ? classes.active : ""
            }`}
            component={NavLink}
            path="/workflowDefs"
          >
            Definitions
          </Button>
        )}
        {isTabActive(tabsList?.["Task Queues"]) && (
          <Button
            id="tskqueTab"
            className={`${classes.navBtn} ${
              activeNav === "tskque" ? classes.active : ""
            }`}
            component={NavLink}
            path="/taskQueue"
          >
            Task Queues
          </Button>
        )}
        {isTabActive(tabsList?.Workbench) && (
          <Button
            id="wbTab"
            className={`${classes.navBtn} ${
              activeNav === "wb" ? classes.active : ""
            }`}
            component={NavLink}
            path="/workbench"
          >
            Workbench
          </Button>
        )}
        {isTabActive(tabsList?.Admin) && (
          <Button
            id="adminTab"
            className={`${classes.navBtn} ${
              activeNav === "admin" ? classes.active : ""
            }`}
            component={NavLink}
            path="/admin"
          >
            Admin
          </Button>
        )}
      </>
    );
  }, [tabsList, activeNav, isTabActive, classes.navBtn, classes.active]);

  return (
    <>
      {loadingState.isLoading ? (
        <DefaultLoader
          displayModal={loadingState.isLoading}
          message={
            !requestID && loadingState.view === "TICKET_SUMMARY"
              ? "Workflow is getting loaded"
              : !requestID && loadingState.view === "FLOW_MAIN"
              ? "Your app is being loaded"
              : ""
          }
        />
      ) : requestID && loadingState.view === "TICKET_SUMMARY" ? (
        <div className={classes.body2}>
          <Switch>
            <Redirect exact from="/" to="ticket_summary" />
            <Route path="/ticket_summary/:id" component={OnlyExecutionPage} />
          </Switch>
        </div>
      ) : loadingState.view === "FLOW_MAIN" ? (
        <>
          <AppBar
            position="fixed"
            style={{
              justifyContent: "flex-start",
              boxShadow:
                "0 1px 2px 0 rgb(0 0 0 / 10%), 0 0 2px 0 rgb(0 0 0 / 10%)",
            }}
            id="main-header"
          >
            <Toolbar classes={{ regular: classes.toolbarRegular }}>
              {renderNavButtons}
            </Toolbar>
          </AppBar>
          <div className={classes.body} id="main-body">
            {tabsError ? (
              <ErrorScreen
                title="Error Loading Application"
                message={getSafeMessage(tabsError?.message || tabsError)}
                actionLabel="Retry"
                onAction={() => window.location.reload()}
              />
            ) : !tabsList ? (
              <DefaultLoader
                displayModal={true}
                message="Loading application..."
              />
            ) : (
              <Switch>
                <Route
                  path="/no-access"
                  key="no-access"
                  render={(props) => {
                    return (
                      <AccessDenied message="No accessible tabs available. Please contact your administrator." />
                    );
                  }}
                />
                <Redirect
                  exact
                  from="/"
                  to={(() => {
                    if (flowRedirectUrl) {
                      return flowRedirectUrl;
                    }
                    const firstAvailable = getFirstAvailableRoute;
                    const finalRoute = firstAvailable || "/no-access";
                    return finalRoute;
                  })()}
                />
                {renderRoutes}
              </Switch>
            )}
          </div>
        </>
      ) : (
        <div className="custom_modal">
          <div className="custom_modal-content">
            <div className="custom_modal-flex">
              <div className="custom_modal_static-msg">
                You do not have access to this page through this link. Please
                contact your admin.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
